import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";
import { OBJECTIVES, type StrategyInputs, type StrategyOutput } from "@shared/strategy.ts";

const FIELDS: { key: keyof StrategyInputs; label: string; area?: boolean }[] = [
  { key: "product", label: "สินค้า/บริการ" },
  { key: "price", label: "ราคา" },
  { key: "budget", label: "งบการตลาด" },
  { key: "market", label: "ตลาด/พื้นที่" },
  { key: "target", label: "กลุ่มเป้าหมาย" },
  { key: "trafficSource", label: "แหล่ง Traffic" },
  { key: "salesChannel", label: "ช่องทางขาย" },
  { key: "offer", label: "ข้อเสนอ (Offer)", area: true },
  { key: "currentProblem", label: "ปัญหาปัจจุบัน", area: true },
];

export default function Strategy() {
  const { activeBrand } = useBrands();
  const [inp, setInp] = useState<StrategyInputs>({ objective: OBJECTIVES[0] });
  const [out, setOut] = useState<StrategyOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => { load(); }, [activeBrand?.id]);
  async function load() { if (!activeBrand) return setHistory([]); const r = await api.get<{ strategies: any[] }>(`/strategy?brandId=${activeBrand.id}`); setHistory(r.strategies); }
  const set = (k: keyof StrategyInputs, v: string) => setInp((p) => ({ ...p, [k]: v }));

  async function generate() {
    if (!activeBrand) { setErr("เลือกแบรนด์ก่อน"); return; }
    setErr(null); setLoading(true); setOut(null); setSaved(false);
    try {
      const r = await api.post<any>("/strategy/generate", { brandId: activeBrand.id, inputs: inp });
      if (r.error === "ai_unparsable") { setErr("AI ตอบไม่เป็นรูปแบบ ลองใหม่"); return; }
      setOut(r.output); setIsMock(r.isMock); load();
    } catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); }
    finally { setLoading(false); }
  }
  async function open(id: string) { setLoading(true); try { const r = await api.get<{ strategy: any }>(`/strategy/${id}`); setOut(r.strategy.output); setIsMock(false); } finally { setLoading(false); } }
  async function saveLearning() {
    if (!activeBrand || !out) return;
    await api.post("/learnings", { brandId: activeBrand.id, type: "ads", title: `กลยุทธ์: ${out.positioning.slice(0, 50)}`, sourceModule: "strategy", confidence: "medium" });
    setSaved(true);
  }

  return (
    <div className="page">
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">วางกลยุทธ์การตลาด <span className="mh-muted mh-eng" style={{ fontSize: 15 }}>Strategy Wizard</span></div>
          <div className="page__subtitle">{activeBrand ? <>บริบท: <b>{activeBrand.name}</b> — กลยุทธ์ครบวงจร + แผน 30 วัน</> : "เลือกแบรนด์ก่อน"}</div>
        </div>
        <Icon name="Target" size={22} />
      </div>

      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <div className="mh-field" style={{ margin: "0 0 14px" }}>
          <label className="mh-label">วัตถุประสงค์หลัก (Objective)</label>
          <select className="mh-select" value={inp.objective} onChange={(e) => set("objective", e.target.value)}>
            {OBJECTIVES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div className="mh-help" style={{ marginBottom: 10 }}>ช่องว่างดึงจาก Brand Brain อัตโนมัติ</div>
        <div className="form-grid">
          {FIELDS.map((f) => (
            <div className="mh-field" style={{ margin: 0, gridColumn: f.area ? "1 / -1" : undefined }} key={f.key}>
              <label className="mh-label" style={{ fontSize: 12.5 }}>{f.label}</label>
              {f.area
                ? <textarea className="mh-textarea" style={{ minHeight: 52 }} value={(inp[f.key] as string) || ""} onChange={(e) => set(f.key, e.target.value)} />
                : <input className="mh-input" value={(inp[f.key] as string) || ""} onChange={(e) => set(f.key, e.target.value)} />}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={generate} disabled={loading}>
            {loading ? <span className="spinner" /> : <><Icon name="Target" size={18} /> วางกลยุทธ์</>}
          </button>
          {history.length > 0 && (
            <select className="mh-select" style={{ width: "auto" }} defaultValue="" onChange={(e) => e.target.value && open(e.target.value)}>
              <option value="">📁 กลยุทธ์เดิม ({history.length})</option>
              {history.map((h) => <option key={h.id} value={h.id}>{h.title}</option>)}
            </select>
          )}
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>

      {loading && <div className="empty"><span className="spinner spinner--dark" /><p className="mh-sec">กำลังวางกลยุทธ์...</p></div>}

      {out && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>กลยุทธ์จาก <b>Mock</b> — ใส่ Claude Key เพื่อกลยุทธ์เชิงลึกจริง</div></div>}

          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <div className="mh-spread" style={{ marginBottom: 10 }}>
              <h3 style={{ fontSize: 15 }}>ภาพรวมกลยุทธ์</h3>
              <button className="mh-btn mh-btn--subtle mh-btn--sm" onClick={saveLearning} disabled={saved}>{saved ? "บันทึกแล้ว ✓" : <><Icon name="Save" size={15} /> Save</>}</button>
            </div>
            <Row k="สถานการณ์ตลาด" v={out.marketSituation} />
            <Row k="Segment เป้าหมาย" v={out.targetSegment} />
            <Row k="Persona" v={out.persona} />
            <Row k="Positioning" v={out.positioning} />
            <Row k="Core Message" v={out.coreMessage} highlight />
          </div>

          <h3 style={{ fontSize: 15, margin: "18px 0 12px" }}>กลยุทธ์รายด้าน</h3>
          <div className="cards">
            <StratCard icon="Sparkles" title="Offer" v={out.offerStrategy} />
            <StratCard icon="PenLine" title="Content" v={out.contentStrategy} />
            <StratCard icon="Image" title="Creative" v={out.creativeStrategy} />
            <StratCard icon="Megaphone" title="Ads" v={out.adsStrategy} />
            <StratCard icon="Radar" title="Channel" v={out.channelStrategy} />
            <StratCard icon="Filter" title="Funnel" v={out.funnel} />
            <StratCard icon="TrendingUp" title="Retargeting" v={out.retargeting} />
          </div>

          <div className="form-grid" style={{ marginTop: 18, alignItems: "start" }}>
            <div className="mh-card card-pad">
              <h3 style={{ fontSize: 14, marginBottom: 10 }}>📊 KPIs</h3>
              {out.kpis.map((k, i) => <div key={i} className="mh-spread" style={{ padding: "7px 0", borderBottom: "1px solid var(--border)" }}><span>{k.metric}</span><b>{k.target}</b></div>)}
            </div>
            <div className="mh-card card-pad">
              <h3 style={{ fontSize: 14, marginBottom: 10 }}>🧪 Testing Plan</h3>
              <ul style={{ margin: 0, paddingLeft: 18 }}>{out.testingPlan.map((t, i) => <li key={i} style={{ marginBottom: 5 }}>{t}</li>)}</ul>
            </div>
          </div>

          <h3 style={{ fontSize: 15, margin: "18px 0 12px" }}>📅 แผน 30 วัน (30-Day Action Plan)</h3>
          <div className="cards">
            {out.actionPlan30.map((w) => (
              <div className="mh-card" key={w.week} style={{ overflow: "hidden" }}>
                <div style={{ padding: "10px 16px", background: "var(--navy-800)", color: "#fff" }}>
                  <b>สัปดาห์ {w.week}</b> <span style={{ color: "var(--text-on-navy-muted)", fontSize: 13 }}>· {w.focus}</span>
                </div>
                <div className="card-pad"><ul style={{ margin: 0, paddingLeft: 18 }}>{w.tasks.map((t, i) => <li key={i} style={{ marginBottom: 5, fontSize: 14 }}>{t}</li>)}</ul></div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ k, v, highlight }: { k: string; v: string; highlight?: boolean }) {
  return (
    <div style={{ marginBottom: 10, padding: highlight ? "10px 12px" : 0, background: highlight ? "var(--blue-050)" : undefined, borderRadius: highlight ? 8 : 0 }}>
      <div className="mh-muted" style={{ fontSize: 12.5 }}>{k}</div>
      <div style={{ fontSize: 14.5, fontWeight: highlight ? 700 : 400 }}>{v}</div>
    </div>
  );
}

function StratCard({ icon, title, v }: { icon: string; title: string; v: string }) {
  return (
    <div className="mh-card card-pad">
      <div className="mh-row" style={{ gap: 8, marginBottom: 8, fontWeight: 700 }}><span className="qa__icon" style={{ width: 30, height: 30 }}><Icon name={icon} size={16} /></span> {title}</div>
      <div style={{ fontSize: 13.8, lineHeight: 1.6 }}>{v}</div>
    </div>
  );
}
